// Task 24575 — email template placeholders.
//
// Templates are rendered server-side, so the tokens that are still written as
// {{Something}} by the time the template reaches the compose modal are the ones
// the backend could not resolve for this record. Rather than block the send and
// leave the user hunting through the body for them, we pull them out, ask for a
// value once, and substitute them in place.
//
// The email body is a Lexical payload, not plain text: it is a JSON string that
// is either the editor state itself or a { json, html } wrapper around it (see
// Components/RichTextEditor/plugins/LoadInitialContent.js). Substituting by
// blind string replace would risk producing invalid JSON, so we parse the
// payload, walk it, and re-stringify.

// A token is {{ anything-without-braces }}. Whitespace inside the braces is
// tolerated so {{ FirstName }} and {{FirstName}} are the same placeholder.
export const PLACEHOLDER_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Unique placeholder names found in one or more strings, in first-seen order.
 * Accepts the raw Lexical payload as-is: `{{` and `}}` are never escaped by
 * JSON.stringify, so a plain scan over the serialised string finds them.
 *
 * @param {string|string[]} sources
 * @returns {string[]} e.g. ["FirstName", "Site Address"]
 */
export const extractPlaceholders = (sources) => {
  const list = Array.isArray(sources) ? sources : [sources];
  const found = [];

  list.forEach((source) => {
    if (!source || typeof source !== "string") return;
    // Fresh lastIndex each pass — PLACEHOLDER_PATTERN is a shared /g regex.
    PLACEHOLDER_PATTERN.lastIndex = 0;
    let match;
    while ((match = PLACEHOLDER_PATTERN.exec(source)) !== null) {
      const name = (match[1] || "").trim();
      if (name && !found.includes(name)) found.push(name);
    }
  });

  return found;
};

/**
 * Replace {{tokens}} in a plain string. A token with no value (missing or
 * blank) is left alone so a half-completed fill never silently blanks text.
 */
export const replacePlaceholdersInText = (text, values) => {
  if (!text || typeof text !== "string") return text;
  return text.replace(PLACEHOLDER_PATTERN, (match, name) => {
    const key = (name || "").trim();
    const value = values?.[key];
    return value === undefined || value === null || value === ""
      ? match
      : String(value);
  });
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const escapeValues = (values) =>
  Object.keys(values || {}).reduce((out, key) => {
    out[key] = escapeHtml(values[key]);
    return out;
  }, {});

// Replace inside every string held anywhere in a parsed structure. Keys like
// "type" or "format" can never contain braces, so scanning them costs nothing.
const replaceDeep = (node, values) => {
  if (typeof node === "string") return replacePlaceholdersInText(node, values);
  if (Array.isArray(node)) return node.map((child) => replaceDeep(child, values));
  if (node && typeof node === "object") {
    return Object.keys(node).reduce((out, key) => {
      out[key] = replaceDeep(node[key], values);
      return out;
    }, {});
  }
  return node;
};

/**
 * Replace {{tokens}} in a Lexical editor payload, preserving its shape so it
 * can be handed straight back to <RichTextEditor receivedContent={...} />.
 * Falls back to a plain string replace for anything that is not JSON (e.g. a
 * template whose BodyHtml is raw HTML).
 */
export const replacePlaceholdersInEditorContent = (content, values) => {
  if (!content || typeof content !== "string") return content;

  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch (error) {
    return replacePlaceholdersInText(content, values);
  }

  if (!parsed || typeof parsed !== "object") {
    return replacePlaceholdersInText(content, values);
  }

  // { json: "<stringified editor state>", html: "<rendered html>" }
  if (typeof parsed.json === "string") {
    let nextJson;
    try {
      nextJson = JSON.stringify(replaceDeep(JSON.parse(parsed.json), values));
    } catch (error) {
      nextJson = replacePlaceholdersInText(parsed.json, values);
    }
    return JSON.stringify({
      ...parsed,
      json: nextJson,
      // The editor regenerates this from the state above as soon as the
      // payload is loaded back in, but keep it in step — and escape, since a
      // value typed by a user lands in markup here, not in a text node.
      html:
        typeof parsed.html === "string"
          ? replacePlaceholdersInText(parsed.html, escapeValues(values))
          : parsed.html,
    });
  }

  // A bare editor state object.
  return JSON.stringify(replaceDeep(parsed, values));
};

/**
 * Turn a token name into something readable for a form label:
 * "Customer_FullName" -> "Customer Full Name".
 */
export const humanisePlaceholder = (name) =>
  String(name || "")
    .replace(/[_.-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
